import { frontendActions, getConfig } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi, CmcWebApiCommon, CmcWebApiTypes, webTradingApi } from '@cmctechnology/webinvest-api-client-web-trading';
import dayjs from 'dayjs';
import { Dispatch } from '@reduxjs/toolkit';
import { ApiRequest } from './models/apiRequest';
import { actions, CLIENT_STORE_NAME, IStore } from './store';
import { ClientFeedbackAddRequest1, NameValuePair, OperationResultModel1, SalesforceCaseAddRequest1, SigninRequestType } from './types';
import { LoginApiModelResult } from './models/apiResponse';
import { marketDataWsActions } from '@cmctechnology/webinvest-api-client-market-data-ws';
import { SOURCES } from '@cmctechnology/webinvest-constants';
import { getNextStep } from './hooks/useJourneyHandler';
import { JourneyTree } from './constants/FREnums';
import { CMCFRSDK, useFRConfig } from './utils/FRConfigSetup';
import { logger, trace } from './utils';

const SEPARATOR = `__`;
const SOURCE = SOURCES.CmcPublic;
const INTERNAL_SERVER_ERROR_STATUS_CODE = 500;
export const usersApiActionKey = (key: ApiRequest) => `${CLIENT_STORE_NAME}${SEPARATOR}${key}`;

export const fetchClientDetails = (force: boolean = false, immediateDispatch: boolean = false, pingSession: boolean = false) => {
  return async (dispatch: Dispatch<any>, getState: () => IStore) => {
    try {
      // check if client details already retrieved
      if (getState()[CLIENT_STORE_NAME].crc && !force) return;

      const request: CmcWebApi.Client.ClientDetailsRequest1 = { Source: '' };
      const response = await frontendActions.webTradingApiCall<
        CmcWebApi.Client.ClientDetailsModel2 & {
          IntermediaryHasAdvisorFeeds: boolean;
          IntermediaryReportCount: number;
          SiteVersion: string;
          MachineName: string;
        }
      >(CLIENT_STORE_NAME, ApiRequest.ClientDetailsGet1, request, dispatch, undefined, undefined, undefined, undefined, immediateDispatch, pingSession);
      dispatch<any>(actions.detailsFetched(response));
    } catch {}
  };
};

export const fetchInternationalCountries = () => {
  return async (dispatch: Dispatch<any>, getState: () => IStore) => {
    if (getState().client?.availableCountries) {
      return;
    }

    try {
      const request = {};
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Client.InternationalCountriesModel1>(
        CLIENT_STORE_NAME,
        ApiRequest.InternationalCountriesGet1,
        request,
        dispatch
      );
      dispatch<any>(actions.internationalCountriesFetched(response));
    } catch {}
  };
};

export const updateSelectedCountry = (alpha3Code: string) => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const request = { CountryIsoCode: alpha3Code };
      await frontendActions.webTradingApiCall(
        CLIENT_STORE_NAME,
        ApiRequest.SelectedCountryUpdate1,
        request,
        dispatch,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      );
      dispatch<any>(actions.selectedCountriesUpdated(alpha3Code));
    } catch {}
  };
};

export const fetchClientRoles = (force: boolean = false, immediateDispatch: boolean = false, pingSession: boolean = false) => {
  return async (dispatch: Dispatch<any>, getState: () => IStore) => {
    try {
      // check if client roles already retrieved
      if (getState()[CLIENT_STORE_NAME].roles.length > 0 && !force) return;

      const request: CmcWebApi.Client.ClientRolesGetRequest1 = { RoleFilters: [] };
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Client.ClientRolesModel1>(
        CLIENT_STORE_NAME,
        ApiRequest.ClientRolesGet1,
        request,
        dispatch,
        undefined,
        undefined,
        undefined,
        undefined,
        immediateDispatch,
        pingSession
      );
      dispatch<any>(actions.rolesFetched(response));
    } catch {}
  };
};

export const unauthorise = () => {
  return actions.unauthorised();
};

export const setForceLogout = (logout: boolean) => {
  return actions.forceLogout(logout);
};

export const frSignOut = async () => {
  useFRConfig(JourneyTree.FR_JOURNEY_LOGOUT);

  try {
    await getNextStep(undefined, JourneyTree.FR_JOURNEY_LOGOUT);
  } catch {}

  try {
    await CMCFRSDK.FRUser.logout();
  } catch {}
};

export const signOut = () => {
  const apiKey = usersApiActionKey(ApiRequest.SignOut);
  logger.log(`signOut being called`);

  return async (dispatch: Dispatch<any>) => {
    logger.log(`from signOut: dispatching unauthorise`);
    dispatch(unauthorise());
    logger.log(`from signOut: dispatching setForceLogout`);
    dispatch(setForceLogout(false));
    dispatch<any>(frontendActions.apiRequestSent(apiKey));

    try {
      logger.log(`from signOut: calling frSignOut`);
      await frSignOut();
      logger.log(`from signOut: calling ClientLogout1`);
      await frontendActions.webTradingApiCall(CLIENT_STORE_NAME, ApiRequest.ClientLogout1, {}, dispatch);
      dispatch<any>(frontendActions.apiRequestSucceeded(apiKey));
    } catch {
      dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: LoginApiModelResult.Error }));
    }
  };
};

const TOKEN_INVALID = `Token invalid`;
const TOKEN_INVALID_WAIT = 1000;

const signIn = (signInRequest: Partial<CmcWebApi.Client.AuthenticationRequest2>) => {
  return async (dispatch: Dispatch<any>) => {
    const apiKey = usersApiActionKey(ApiRequest.SignIn);
    dispatch<any>(frontendActions.apiRequestSent(apiKey));

    try {
      const { stockbrokingApiUrl } = getConfig();
      let signinResponse = await webTradingApi(stockbrokingApiUrl).send<CmcWebApi.Client.AuthenticationResponseModel3>(
        { [ApiRequest.ClientAuthenticate3]: signInRequest },
        true,
        undefined,
        true,
        true
      );

      // If token invalid try again, since it could be nbf check fail
      if (signinResponse.Result === LoginApiModelResult.Error && signinResponse.ErrorMessage === TOKEN_INVALID) {
        trace(ApiRequest.ClientAuthenticate3, signinResponse);
        await new Promise((resolve) => setTimeout(resolve, TOKEN_INVALID_WAIT));

        signinResponse = await webTradingApi(stockbrokingApiUrl).send<CmcWebApi.Client.AuthenticationResponseModel3>(
          { [ApiRequest.ClientAuthenticate3]: signInRequest },
          true,
          undefined,
          true,
          true
        );
      }

      const { Result } = signinResponse;

      if (Result === LoginApiModelResult.Error || Result === LoginApiModelResult.Unknown || Result === LoginApiModelResult.SetPasswordRequired) {
        dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: Result }));
        return;
      }

      if (Result === LoginApiModelResult.InterceptorRequired) {
        const { FormInterceptors } = signinResponse;

        if (!FormInterceptors || FormInterceptors.length === 0) {
          dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: Result }));
          return;
        }

        dispatch<any>(actions.interceptorsRequired(FormInterceptors));
        dispatch<any>(frontendActions.apiRequestSucceeded(apiKey));
        return;
      }

      await dispatch<any>(fetchClientRoles(false, true, true));
      await dispatch<any>(fetchClientDetails(false, true, true));

      // Delay notifying everyone that we are authenticated to avoid calling backend before session is properly stored
      await new Promise((resolve) => setTimeout(resolve, TOKEN_INVALID_WAIT));

      dispatch<any>(actions.authenticatedAtUpdated(dayjs().unix()));
      dispatch<any>(actions.userInfoUpdated({ username: signInRequest.LoginCode! }));
      dispatch<any>(frontendActions.apiRequestSucceeded(apiKey));
    } catch {
      dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: LoginApiModelResult.Error }));
    }
  };
};

const buildSigninRequest = (request: Partial<CmcWebApi.Client.AuthenticationRequest2>) => {
  return { ...request };
};

export const signInWithUsername = (username: string, password: string, shiftKey: boolean, source: string = SOURCE) => {
  const signInRequest = {
    LoginCode: username,
    Password: password,
    RequestType: SigninRequestType.Login,
    ShiftKey: shiftKey,
    Source: source
  };
  return signIn(buildSigninRequest(signInRequest));
};

export const signInWithToken = (token: string, shiftKey: boolean, source: string = SOURCE) => {
  const signInRequest = {
    RequestType: SigninRequestType.TokenAuthorization,
    Token: token,
    ShiftKey: shiftKey,
    Source: source
  };
  return signIn(buildSigninRequest(signInRequest));
};

export const forgotPassword = (username: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: Partial<CmcWebApi.Client.ForgottenPasswordAuthorisationCodeRequest1> = {
      LoginCode: username
    };

    try {
      const result = await frontendActions.webTradingApiCall<{ Success: boolean }>(
        CLIENT_STORE_NAME,
        ApiRequest.ForgotPassword,
        request,
        dispatch,
        undefined,
        true
      );

      return result.Success;
    } catch {
      return false;
    }
  };
};

export const forgotPasswordSubmit = (username: string, password: string, code: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: Partial<CmcWebApi.Client.ForgottenPasswordResetRequest1> = {
      AuthorisationCode: code,
      ConfirmPassword: password,
      LoginCode: username,
      NewPassword: password
    };

    try {
      const result = await frontendActions.webTradingApiCall<CmcWebApi.Client.ForgottenPasswordResetModel1>(
        CLIENT_STORE_NAME,
        ApiRequest.ForgotPasswordSubmit,
        request,
        dispatch,
        undefined,
        true
      );

      return result;
    } catch (error: any) {
      return { Result: error.toString() } as Partial<CmcWebApi.Client.ForgottenPasswordResetModel1>;
    }
  };
};

export const validateAuthorisationCode = (username: string, code: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: Partial<CmcWebApi.Client.ValidateAuthorisationCodeRequest1> = {
      AuthorisationCode: code,
      LoginCode: username
    };

    try {
      const result = await frontendActions.webTradingApiCall<{ Success: boolean }>(
        CLIENT_STORE_NAME,
        ApiRequest.ValidateAuthorisationCode1,
        request,
        dispatch,
        undefined,
        true
      );

      return { success: result.Success };
    } catch (error: any) {
      console.log(error.toString());
      return { success: false };
    }
  };
};

export const requestPasswordReset = (username: string, code: string, password: string) => {
  return async (dispatch: Dispatch<any>) => {
    const request: Partial<CmcWebApi.Client.ForgottenPasswordResetRequest1> = {
      AuthorisationCode: code,
      LoginCode: username,
      NewPassword: password,
      ConfirmPassword: password
    };

    try {
      const result = await frontendActions.webTradingApiCall<{ Success: boolean; UiMessage: string }>(
        CLIENT_STORE_NAME,
        ApiRequest.RequestPasswordReset1,
        request,
        dispatch,
        undefined,
        true
      );

      return {
        success: result.Success,
        message: result.UiMessage
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.toString()
      };
    }
  };
};

export const processInterceptor = (
  ruleName: string,
  username: string,
  password: string,
  shiftKey: boolean,
  formData: NameValuePair[],
  source: string = SOURCE
) => {
  return async (dispatch: Dispatch<any>) => {
    const apiKey = usersApiActionKey(ApiRequest.InterceptorProcess1);
    dispatch<any>(frontendActions.apiRequestSent(apiKey));

    const interceptorProcessRequest: Partial<CmcWebApi.Client.InterceptorRequest1> = {
      LoginCode: username,
      Password: password,
      Source: source,
      RequestType: SigninRequestType.Login,
      ShiftKey: shiftKey,
      RuleName: ruleName,
      FormData: formData
    };

    try {
      const { stockbrokingApiUrl } = getConfig();
      const interceptorProcessResponse = await webTradingApi(stockbrokingApiUrl).send<OperationResultModel1>(
        { [ApiRequest.InterceptorProcess1]: interceptorProcessRequest },
        true
      );

      const { Success, UiMessage } = interceptorProcessResponse;

      if (!Success) {
        dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: UiMessage }));
        return;
      }
      dispatch<any>(frontendActions.apiRequestSucceeded(apiKey));
    } catch {
      dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: String(INTERNAL_SERVER_ERROR_STATUS_CODE) }));
    }
  };
};

export const processInterceptorWithToken = (ruleName: string, token: string, formData: NameValuePair[], source: string = SOURCE) => {
  return async (dispatch: Dispatch<any>) => {
    const apiKey = usersApiActionKey(ApiRequest.InterceptorTokenProcess);
    dispatch<any>(frontendActions.apiRequestSent(apiKey));

    const interceptorTokenRequest: Partial<CmcWebApi.Client.InterceptorTokenRequest> = {
      Token: token,
      Source: source,
      RuleName: ruleName,
      FormData: formData
    };

    try {
      const { stockbrokingApiUrl } = getConfig();
      const interceptorProcessResponse = await webTradingApi(stockbrokingApiUrl).send<OperationResultModel1>(
        { [ApiRequest.InterceptorTokenProcess]: interceptorTokenRequest },
        true
      );

      const { Success, UiMessage } = interceptorProcessResponse;

      if (!Success) {
        dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: UiMessage }));
        return;
      }
      dispatch<any>(frontendActions.apiRequestSucceeded(apiKey));
    } catch {
      dispatch<any>(frontendActions.apiRequestFailed({ apiRequest: apiKey, errorCode: String(INTERNAL_SERVER_ERROR_STATUS_CODE) }));
    }
  };
};

export const getJwtToken = async (dispatch: Dispatch) => {
  const signInRequest: Partial<CmcWebApi.Client.AuthenticationRequest2> = {
    Source: SOURCE,
    RequestType: SigninRequestType.AcquireSession
  };

  const response = await frontendActions.webTradingApiCall<CmcWebApi.Client.AuthenticationResponseModel3>(
    CLIENT_STORE_NAME,
    ApiRequest.ClientAuthenticate3,
    signInRequest,
    dispatch,
    SigninRequestType.AcquireSession,
    true
  );

  return response.Token;
};

export const connectToWebSocket = () => {
  return async (dispatch: Dispatch) => {
    dispatch<any>(actions.wsAuthenticated(false));
    // TODO put a policy in here to back off if connection continually fails. eg. server is down or not enough heartbeats
    dispatch<any>(marketDataWsActions.connect({ wsUrl: getConfig().marketDataWsUrl }));
  };
};

export const authenticateToWebSocket = () => {
  return async (dispatch: Dispatch) => {
    const token = await getJwtToken(dispatch);
    dispatch<any>(marketDataWsActions.send({ authenticateV1: { token } }));
    dispatch<any>(actions.wsAuthenticated(true));
  };
};

export const updateClientDetails = (request: Partial<CmcWebApi.Client.ClientDetailsUpdateRequest2>) => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Client.ClientDetailsUpdateModel2>(
        CLIENT_STORE_NAME,
        ApiRequest.ClientDetailsUpdate2,
        request,
        dispatch
      );
      dispatch<any>(actions.clientDetailsUpdated({ result: response, updateDetails: request }));
    } catch {}
  };
};

export const disableClientFalconAccess = () => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        CLIENT_STORE_NAME,
        ApiRequest.ClientDisableFalconAccess1,
        {},
        dispatch
      );
      dispatch<any>(actions.clientFalconAccessDisabled({ result: response }));
      return response;
    } catch {}
  };
};

export const addClientFeedback = (request: ClientFeedbackAddRequest1) => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        CLIENT_STORE_NAME,
        ApiRequest.ClientFeedbackAdd1,
        request,
        dispatch
      );
      return response;
    } catch {}
  };
};

export const sessionCheck = () => {
  return async (dispatch: Dispatch<any>) => {
    try {
      await frontendActions.webTradingApiCall<CmcWebApiTypes.Types.WebApiResponse>(CLIENT_STORE_NAME, ApiRequest.SessionCheck1, {}, dispatch);
    } catch {}
  };
};

export const addSalesforceCase = (request: SalesforceCaseAddRequest1) => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.OperationResultModel1>(
        CLIENT_STORE_NAME,
        ApiRequest.SalesforceCaseAdd1,
        request,
        dispatch
      );
      return response;
    } catch {}
  };
};

export { subscribePendingOnlineApplications, unsubscribePendingOnlineApplications } from './pendingOnlineApplicationActions';
export { fetchStockQueries, fetchRecentStocks } from './stockQueryActions';
export { fetchClientProfileSettings, subscribeClientProfileSettings, unsubscribeClientProfileSettings } from './profileSettingActions';
